import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useRef, useCallback, useState } from "react";
import { getAuth } from "firebase/auth";

/** Minimum time between completed analyses (reduces API cost / spam). */
const COOLDOWN_MS = 60 * 1000;

/** Ignore repeat trigger attempts within this window (double-click / rapid taps). */
const TRIGGER_DEBOUNCE_MS = 800;

/**
 * Custom hook for analyzing progress photos with AI
 * Cooldown after each successful run + debounced trigger to avoid duplicate requests.
 * @param {string} clientId - Client ID for photo analysis
 */
export const usePhotoAnalysis = (clientId) => {
  const queryClient = useQueryClient();
  const lastAnalysisTimeRef = useRef(null);
  const lastTriggerAttemptRef = useRef(0);
  const busyRef = useRef(false);
  const [isInvoking, setIsInvoking] = useState(false);
  const auth = getAuth();

  /**
   * Wait for authentication state to be ready
   */
  const waitForAuth = useCallback(async () => {
    return new Promise((resolve, reject) => {
      // If user is already available, resolve immediately
      if (auth.currentUser) {
        resolve(auth.currentUser);
        return;
      }

      // Set a timeout to avoid infinite waiting
      const timeout = setTimeout(() => {
        unsubscribe();
        reject(new Error("Authentication timeout - user not authenticated"));
      }, 5000); // 5 second timeout

      // Listen for auth state changes
      const unsubscribe = auth.onAuthStateChanged((user) => {
        clearTimeout(timeout);
        unsubscribe();

        if (user) {
          resolve(user);
        } else {
          reject(new Error("User not authenticated"));
        }
      });
    });
  }, [auth]);

  /**
   * Get authentication headers with current user token
   */
  const getAuthHeaders = useCallback(async () => {
    try {
      const user = await waitForAuth();
      const token = await user.getIdToken();

      return {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      };
    } catch (error) {
      throw new Error("User not authenticated");
    }
  }, [waitForAuth]);

  // Query for cached analysis results
  const analysisQuery = useQuery({
    queryKey: ["photoAnalysis", clientId],
    queryFn: async () => {
      if (!clientId) {
        throw new Error("Client ID is required");
      }

      const cloudFunctionUrl = "https://app.mighty45.com";
      const headers = await getAuthHeaders();

      const response = await fetch(
        `${cloudFunctionUrl}/api/v1/analyze/progress-photos`,
        {
          method: "POST",
          headers,
          body: JSON.stringify({
            client_id: clientId,
          }),
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.error || `HTTP error! status: ${response.status}`,
        );
      }

      const result = await response.json();

      // Update the last analysis time
      lastAnalysisTimeRef.current = Date.now();

      return result;
    },
    enabled: false, // Don't auto-fetch, only fetch when triggered
    staleTime: 5 * 60 * 1000, // 5 minutes - keep data fresh for 5 minutes
    cacheTime: 30 * 60 * 1000, // 30 minutes - keep in cache for 30 minutes
    retry: (failureCount, error) => {
      // Don't retry on 4xx errors (client errors)
      if (error.message.includes("400") || error.message.includes("404")) {
        return false;
      }
      return failureCount < 2; // Only retry once
    },
  });

  // Mutation for triggering new analysis
  const analysisMutation = useMutation({
    mutationFn: async (clientId) => {
      if (!clientId) {
        throw new Error("Client ID is required");
      }

      const cloudFunctionUrl = "https://app.mighty45.com";
      const headers = await getAuthHeaders();

      const response = await fetch(
        `${cloudFunctionUrl}/api/v1/analyze/progress-photos`,
        {
          method: "POST",
          headers,
          body: JSON.stringify({
            client_id: clientId,
          }),
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.error || `HTTP error! status: ${response.status}`,
        );
      }

      return response.json();
    },
    onSuccess: (data) => {
      // Update the cached data
      queryClient.setQueryData(["photoAnalysis", clientId], data);
      // Update the last analysis time
      lastAnalysisTimeRef.current = Date.now();
    },
  });

  const isAnalyzing = isInvoking || analysisMutation.isPending;

  // Cooldown satisfied and not currently running (for enabling the button)
  const canAnalyze = useCallback(() => {
    if (isAnalyzing) {
      return false;
    }
    if (!lastAnalysisTimeRef.current) {
      return true;
    }
    const timeSinceLastAnalysis = Date.now() - lastAnalysisTimeRef.current;
    return timeSinceLastAnalysis >= COOLDOWN_MS;
  }, [isAnalyzing]);

  // Get time remaining until next analysis is allowed (ms)
  const getTimeUntilNextAnalysis = useCallback(() => {
    if (!lastAnalysisTimeRef.current) {
      return 0;
    }
    const timeSinceLastAnalysis = Date.now() - lastAnalysisTimeRef.current;
    const timeRemaining = COOLDOWN_MS - timeSinceLastAnalysis;
    return Math.max(0, timeRemaining);
  }, []);

  // Human-readable wait time after a completed analysis
  const formatTimeRemaining = useCallback(() => {
    const timeRemaining = getTimeUntilNextAnalysis();
    if (timeRemaining === 0) {
      return null;
    }
    const secTotal = Math.ceil(timeRemaining / 1000);
    if (secTotal < 60) {
      return `${secTotal} second${secTotal !== 1 ? "s" : ""}`;
    }
    const min = Math.ceil(secTotal / 60);
    return `${min} minute${min !== 1 ? "s" : ""}`;
  }, [getTimeUntilNextAnalysis]);

  /**
   * Starts analysis when allowed. Returns `null` if a duplicate trigger was ignored
   * (already running or debounced). Throws if cooldown is active.
   */
  const triggerAnalysis = useCallback(async () => {
    if (!clientId) {
      throw new Error("Client ID is required");
    }

    if (busyRef.current || analysisMutation.isPending) {
      return null;
    }

    const cooldownOk =
      !lastAnalysisTimeRef.current ||
      Date.now() - lastAnalysisTimeRef.current >= COOLDOWN_MS;
    if (!cooldownOk) {
      const waitLabel = formatTimeRemaining() || "a moment";
      throw new Error(
        `Analysis was recently performed. Please wait ${waitLabel} before analyzing again.`,
      );
    }

    const now = Date.now();
    if (now - lastTriggerAttemptRef.current < TRIGGER_DEBOUNCE_MS) {
      return null;
    }
    lastTriggerAttemptRef.current = now;

    busyRef.current = true;
    setIsInvoking(true);
    try {
      return await analysisMutation.mutateAsync(clientId);
    } finally {
      busyRef.current = false;
      setIsInvoking(false);
    }
  }, [clientId, formatTimeRemaining, analysisMutation]);

  // Get cached analysis data
  const getCachedAnalysis = useCallback(() => {
    return queryClient.getQueryData(["photoAnalysis", clientId]);
  }, [queryClient, clientId]);

  // Force refresh the cached data (bypasses debounce for UI updates)
  const refreshCachedData = useCallback(() => {
    return analysisQuery.refetch();
  }, [analysisQuery]);

  return {
    // Analysis data
    data: analysisQuery.data,
    isLoading: analysisQuery.isLoading || analysisMutation.isPending,
    error: analysisQuery.error || analysisMutation.error,

    // Analysis actions
    triggerAnalysis,
    getCachedAnalysis,
    refreshCachedData,

    // Cooldown utilities
    canAnalyze,
    getTimeUntilNextAnalysis,
    formatTimeRemaining,

    // Status flags (includes debounce / in-flight guard)
    isAnalyzing,
    hasAnalysis: !!analysisQuery.data?.data?.analysis,

    // Raw query objects for advanced usage
    analysisQuery,
    analysisMutation,
  };
};

export default usePhotoAnalysis;
